import clsx from 'clsx'
import PropTypes from 'prop-types'
import ChevronDown from '@/assets/svgs/chevron-down.svg?react'

/**
 * A sidebar item that opens to reveal its own sub-items.
 *
 * Controlled: it holds no open state of its own, because what opens a group is
 * usually the route rather than the click — landing on a page inside it has to
 * paint it open, and only the caller knows that.
 *
 * The header row is `NavigationLink`'s markup, so the two line up in the rail:
 * the same 5px rectangle, 24px icon slot and label. Sub-items pass no icon and
 * are indented by the empty slot the row draws for it, which is why nothing here
 * adds a padding of its own — the labels are long and the rail is 240px, so an
 * extra indent would only clip them.
 */
export const NavigationGroup = ({ icon, label, open, onToggle, active = false, children }) => {
  const headerClass = clsx(
    // NavigationLink's class string minus `no-underline`, which is an <a>
    // concern. `navigation-item` is backed by no CSS — it is the selector the
    // arbitrary variants below key off, and the hook the tests read.
    'navigation-item flex items-center gap-y-3.25 gap-x-2.5 relative w-full cursor-pointer type-body-1 text-grey-50 hover:text-white hover:bg-theme-50',
    // Deliberately not `navigation-item--active`: that is what lights the
    // rectangle and bolds the label, and a group holding the current page is not
    // itself the current page. Marks it for anyone styling the header later.
    active && 'navigation-item__parent--active'
  )

  const onKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      // Space scrolls the page otherwise.
      event.preventDefault()
      onToggle()
    }
  }

  return (
    <>
      {/* A div carrying a click handler is invisible to the keyboard and
          announces as nothing, so the row is given the role, the tab stop and the
          key handling a button would have had. A real <button> would inherit
          styling this row has to control itself. */}
      <div
        className={headerClass}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={onKeyDown}
      >
        <div className="w-1.25 min-h-12 self-stretch" />
        <div className="flex justify-center w-6">{icon}</div>
        <div className="font-avenir relative top-px ml-2.5 flex-1">{label}</div>
        {/* The asset hardcodes a black fill on its inner group, so the caret
            would stay black against the row's hover fill — `fill-current` on the
            polygon itself beats the inherited attribute. */}
        <div
          className={clsx(
            'ml-auto mr-3 flex items-center [&_polygon]:fill-current transition-transform duration-150 ease-in-out',
            open && 'rotate-180'
          )}
          data-testid="navigation-group-chevron"
        >
          <ChevronDown />
        </div>
      </div>

      {/* A sibling of the header, not a child of it: `[.navigation-item--active_&]`
          is a descendant selector, so nesting would let an active sub-item's
          styling be reached from the wrong row. */}
      {open && <div className="flex flex-col">{children}</div>}
    </>
  )
}

NavigationGroup.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  active: PropTypes.bool,
  children: PropTypes.node,
}

export default NavigationGroup
